import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

type State =
  | { status: 'loading' }
  | { status: 'valid' }
  | { status: 'already' }
  | { status: 'error'; message: string }
  | { status: 'success' };

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState<State>({ status: 'loading' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', message: 'Chybí ověřovací token v odkazu.' });
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`,
          { headers: { apikey: SUPABASE_ANON_KEY } }
        );
        const data = await res.json();
        if (!res.ok) {
          setState({ status: 'error', message: data?.error || 'Neplatný odkaz.' });
          return;
        }
        if (data.valid === false && data.reason === 'already_unsubscribed') {
          setState({ status: 'already' });
          return;
        }
        setState({ status: 'valid' });
      } catch {
        setState({ status: 'error', message: 'Spojení se nezdařilo. Zkus to prosím později.' });
      }
    })();
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/handle-email-unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!res.ok) {
        setState({ status: 'error', message: data?.error || 'Akci se nepodařilo dokončit.' });
        return;
      }
      setState({ status: 'success' });
    } catch {
      setState({ status: 'error', message: 'Spojení se nezdařilo.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Odhlášení z odběru | Tom Art Mask</title>
        <meta name="description" content="Odhlášení z odběru emailových notifikací z webu Tom Art Mask." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://tomartmask.cz/unsubscribe" />
      </Helmet>
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <div className="max-w-md w-full bg-card border border-border rounded-md p-8 text-center">
        <p className="text-3xl tracking-wider text-foreground mb-2" style={{ fontFamily: 'Bebas Neue' }}>
          TOM ART MASK
        </p>
        <h1 className="text-xs text-primary uppercase tracking-widest mb-8">Odhlášení z odběru emailů</h1>


        {state.status === 'loading' && (
          <div className="flex flex-col items-center gap-3 py-8">
            <Loader2 className="animate-spin text-primary" size={32} />
            <p className="text-muted-foreground text-sm">Ověřuji odkaz...</p>
          </div>
        )}

        {state.status === 'valid' && (
          <>
            <p className="text-foreground mb-6">
              Opravdu se chceš odhlásit z odběru emailových notifikací z webu Tom Art Mask?
            </p>
            <Button
              onClick={confirm}
              disabled={submitting}
              className="w-full uppercase tracking-widest font-bold"
            >
              {submitting ? 'Odhlašuji...' : 'Potvrdit odhlášení'}
            </Button>
          </>
        )}

        {state.status === 'already' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <CheckCircle2 className="text-primary" size={40} />
            <p className="text-foreground">Tento email už je odhlášený.</p>
          </div>
        )}

        {state.status === 'success' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <CheckCircle2 className="text-primary" size={40} />
            <p className="text-foreground font-medium">Úspěšně odhlášeno</p>
            <p className="text-muted-foreground text-sm">
              Z této adresy ti už nebudeme posílat žádné emaily.
            </p>
          </div>
        )}

        {state.status === 'error' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <XCircle className="text-destructive" size={40} />
            <p className="text-foreground">{state.message}</p>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default Unsubscribe;
